import { saveAs } from "file-saver"

const escapeCell = (value: unknown): string => {
  const text = value === null || value === undefined ? "" : String(value)
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

export const toCsv = (rows: (string | number | null | undefined)[][]): string =>
  rows.map((row) => row.map(escapeCell).join(",")).join("\r\n")

export const downloadCsvString = (fileName: string, csv: string): void => {
  // Prepend BOM so Excel reads UTF-8 correctly.
  const blob = new Blob([`\uFEFF${csv}`], {
    type: "text/csv;charset=utf-8",
  })
  saveAs(blob, fileName)
}

export const downloadCsv = (
  fileName: string,
  rows: (string | number | null | undefined)[][],
): void => {
  downloadCsvString(fileName, toCsv(rows))
}

/**
 * Minimal RFC-4180 CSV parser that supports quoted fields, escaped quotes
 * (""), and both \n and \r\n line endings. Returns a matrix of strings.
 */
export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ""
  let inQuotes = false
  let i = 0
  // Strip a leading UTF-8 BOM if present.
  if (text.charCodeAt(0) === 0xfeff) i = 1

  const pushCell = () => {
    row.push(cell)
    cell = ""
  }
  const pushRow = () => {
    pushCell()
    rows.push(row)
    row = []
  }

  while (i < text.length) {
    const char = text[i]

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i += 2
          continue
        }
        inQuotes = false
        i += 1
        continue
      }
      cell += char
      i += 1
      continue
    }

    if (char === '"') {
      inQuotes = true
      i += 1
      continue
    }
    if (char === ",") {
      pushCell()
      i += 1
      continue
    }
    if (char === "\r") {
      if (text[i + 1] === "\n") i += 1
      pushRow()
      i += 1
      continue
    }
    if (char === "\n") {
      pushRow()
      i += 1
      continue
    }
    cell += char
    i += 1
  }

  // Flush the trailing cell/row unless the file ended with a newline.
  if (cell.length > 0 || row.length > 0) pushRow()

  return rows
}

export const readFileAsText = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
